import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown, ArrowLink } from "components";
import { useAnchor } from "hooks";
import { Title1Content } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Title1Content;
}

const Title1: VFC<Props> = withErrorBoundary(({ content: { title, description, title1Link, linkLabel } }) => {
  useAnchor("title1");

  return (
    <StyledTitle className="title1 Content">
      <h2 className="Font_48_54_600 Color_primary">{title}</h2>
      <Markdown className="Title1__description Font_24_32_450 Color_secondary">{description}</Markdown>
      {title1Link && (
        <ArrowLink href={title1Link} className="Title1__link Font_18_26_600 Color_primary">
          {linkLabel}
        </ArrowLink>
      )}
    </StyledTitle>
  );
}, {});

const StyledTitle = styled.section`
  margin-top: -64px;
  padding-top: 64px;

  .Title1__description {
    max-width: 746px;
    margin-top: 16px;
  }

  .Title1__link {
    position: relative;
    margin-top: 24px;
    padding-bottom: 4px;
    transition: all 0.2s linear;
    text-decoration: none;
    color: #000;

    svg {
      width: 12px;
      height: 12px;
    }

    &::before {
      content: "";
      display: block;
      position: absolute;
      left: 0;
      bottom: 0;
      height: 2px;
      width: 0%;
      transition: width 0s ease;
    }

    &::after {
      content: "";
      display: block;
      position: absolute;
      right: 0;
      bottom: 0;
      height: 2px;
      width: 0%;
      background: ${({ theme }) => theme.colors.fog["80"]};
      transition: width 0.5s ease;
    }

    &:hover {
      &::before {
        width: 100%;
        background: ${({ theme }) => theme.colors.fog["80"]};
        transition: width 0.5s ease;
      }

      &::after {
        width: 100%;
        background: transparent;
        transition: width 0s ease;
      }
    }

    :hover {
      color: ${({ theme }) => theme.colors.fog["80"]};

      svg {
        fill: ${({ theme }) => theme.colors.fog["80"]};
      }
    }
  }
`;

gql`
  fragment Title1Content on ComponentTitleTitle1 {
    title
    description
    title1Link
    linkLabel
  }
`;

export { Title1 };
export type { Props as Title1Props };
